import { useEffect, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useHasPermission, useHasEnvironmentsPermission } from "../../common/Hooks";
import Deployment from "../../services/deployment";
import Environment from "../../services/environment";
import PubSub from "../../services/PubSub";
import Utils from "../../utils/utils";
import AccordionItem from "../controls/AccordionItem";
import { Accordion } from "react-bootstrap";
import CreateDeployment from "../deployment/CreateDeployment";
import Toolbar from "../controls/Toolbar";
import { useTranslation } from "react-i18next";

const EnvironmentList = ({ onClickNew, onClick = () => { } }) => {
    const [translate] = useTranslation();
    const [cursors, setCursors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [environments, setEnvironments] = useState([]);
    const [deployments, setDeployments] = useState([]);
    const [domain, setDomain] = useState(Utils.getDomain());
    const [deploymentParams, setDeploymentParams] = useState({});
    const [allowedToCreateDeployments, setAllowedToCreateDeployments] = useState(false);
    const [environmentParams, setEnvironmentParams] = useState({
        domain,
        pageIncrement: 0
    });
    const [showCreateDeploymentModal, setShowCreateDeploymentModal] = useState(false);
    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: null,
        type: "environment"
    });
    const environmentsPermissions = useHasEnvironmentsPermission({
        domain: Utils.getDomain(),
        environments: environments,
        deployment: null,
        type: "deployment"
    });
    const history = useHistory();

    const showEnvironmentDetails = useCallback((environmentId) => {
        let page = "/environment";
        if (environmentId) {
            page += `/${environmentId}`;
        }
        history.push(page);
        onClick();
    }, [history, onClick]);

    const showDeploymentDetails = useCallback((environmentId, deploymentId) => {
        let page = "/environment";
        if (environmentId) {
            page += `/${environmentId}`;
        }
        if (deploymentId) {
            page += `/deployment/${deploymentId}`;
        }
        history.push(page);
        onClick();
    }, [history, onClick]);

    const getDeployments = useCallback(async (params) => {
        const result = {};
        const promises = params.environments.map(async (environment) => {
            if (environmentsPermissions[environment._id]?.read) {
                const promise = Deployment.getAllDeploymentsInEnvironment(environment._id);
                promise.then(response => {
                    if (response && !response.error) {
                        result[environment._id] = response.data || [];
                    }
                });
                return promise;
            }
            return null;
        });

        await Promise.all(promises).then(() => {
            setDeployments(result);
        });

        let environmentId, deploymentId;
        if (params.deletedDeployment) {
            environmentId = params.deletedDeployment.environment._id;
        }
        else if (params.newDeployment) {
            environmentId = params.newDeployment.environment;
            deploymentId = params.newDeployment._id;
        }
        else if (params.updatedDeployment) {
            environmentId = params.updatedDeployment.environment;
            deploymentId = params.updatedDeployment._id;
        }
        else {
            environmentId = params.focusId;
        }

        if (deploymentId) {
            showDeploymentDetails(environmentId, deploymentId);
        }
        else if (environmentId) {
            showEnvironmentDetails(environmentId);
        }

        setDeploymentParams({});
    }, [showDeploymentDetails, showEnvironmentDetails, environmentsPermissions]);

    const getEnvironments = useCallback(async (params) => {
        setEnvironments([]);
        const response = await Environment.getAllEnvironmentsInDomain(params.domain, null, params.firstCursor, params.lastCursor, params.pageIncrement < 0, params.getLatest);
        if (response && !response.error) {
            setEnvironments(response.data);
            setDeploymentParams({ environments: response.data, focusId: params.newEnvironmentId });
            setCurrentPage(page => {
                const result = params.getLatest ? Math.ceil(response.paging.count / response.paging.pageSize) : params.environmentDeleted ? 1 : page + params.pageIncrement;
                setHasNextPage(response.paging.count > result * response.paging.pageSize);
                return result;
            });
            setCursors({
                firstCursor: response.paging.firstCursor,
                lastCursor: response.paging.lastCursor
            });
        }

        setEnvironmentParams({});
    }, []);

    useEffect(() => {
        if (permissions.read && environmentParams.domain) {
            getEnvironments(environmentParams);
        }
    }, [permissions.read, environmentParams, getEnvironments]);

    useEffect(() => {
        if (deploymentParams.environments?.length) {
            getDeployments(deploymentParams);
        }
    }, [deploymentParams, getDeployments]);

    useEffect(() => {
        let allowedOnAnyEnv = false;
        environments.forEach((environment) => {
            allowedOnAnyEnv = allowedOnAnyEnv || environmentsPermissions[environment._id]?.create;
        })

        setAllowedToCreateDeployments(allowedOnAnyEnv);
    }, [environments, environmentsPermissions, setAllowedToCreateDeployments])

    useEffect(() => {
        const subscribeId = PubSub.subscribe("refresh-environment-list", (params) => {
            setEnvironmentParams({
                domain,
                pageIncrement: 0,
                firstCursor: params?.environmentDeleted ? "" : cursors.firstCursor,
                lastCursor: params?.environmentDeleted ? "" : cursors.lastCursor,
                newEnvironmentId: params?.newEnvironmentId,
                environmentDeleted: params?.environmentDeleted,
                getLatest: !!params?.newEnvironmentId
            });
        });

        return () => {
            PubSub.unsubscribe("refresh-environment-list", subscribeId);
        };
    }, [domain, cursors.firstCursor, cursors.lastCursor]);

    useEffect(() => {
        const subscribeId = PubSub.subscribe("refresh-environment-deployments", (params) => {
            setDeploymentParams({
                environments,
                newDeployment: params?.newDeployment,
                updatedDeployment: params?.updatedDeployment,
                deletedDeployment: params?.deletedDeployment
            });
        });

        return () => {
            PubSub.unsubscribe("refresh-environment-deployments", subscribeId);
        };
    }, [environments]);

    useEffect(() => {
        const subscribeId = PubSub.subscribe("domain", (domain) => {
            setDomain(domain);
        });

        return () => {
            PubSub.unsubscribe("domain", subscribeId);
        };
    }, []);

    const previous = useCallback(() => {
        setEnvironmentParams({
            domain,
            pageIncrement: -1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [domain, cursors.firstCursor, cursors.lastCursor]);

    const next = useCallback(() => {
        setEnvironmentParams({
            domain,
            pageIncrement: 1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [domain, cursors.firstCursor, cursors.lastCursor]);

    const openCreateDeploymentModal = useCallback(() => {
        setShowCreateDeploymentModal(true);
    }, [setShowCreateDeploymentModal]);

    const renderToolbar = useMemo(() => {
        return <Toolbar
            buttons={[
                {
                    name: "ChevronLeft",
                    title: translate("OTM_PREVIOUS"),
                    onClick: previous,
                    disabled: currentPage === 1
                },
                {
                    name: "ChevronRight",
                    title: translate("OTM_NEXT"),
                    onClick: next,
                    disabled: !hasNextPage
                },
                {
                    name: "Add",
                    title: translate("OTM_CREATE_ENVIRONMENT"),
                    onClick: onClickNew,
                    disabled: !permissions.create
                },
                {
                    name: "PlaylistAdd",
                    title: translate("OTM_CREATE_DEPLOYMENT"),
                    onClick: openCreateDeploymentModal,
                    disabled: environments.length === 0 || !allowedToCreateDeployments
                }
            ]}
            heading={translate("OTM_ENVIRONMENTS")}
        />
    }, [previous, currentPage, next, hasNextPage, onClickNew, permissions, translate,
        openCreateDeploymentModal, environments, allowedToCreateDeployments]);

    const renderAccordion = useMemo(() => {
        return environments.map((environment, index) => (
            <AccordionItem
                heading={environment.name}
                content={deployments[environment._id] || []}
                key={index}
                elementId={environment._id}
                onHeaderClick={showEnvironmentDetails}
                onItemClick={showDeploymentDetails}
                noItemMessage={environmentsPermissions[environment._id]?.read ? "OTM_NO_DEPLOYMENTS" : "OTM_NO_DEPLOYMENT_PERMISSIONS"}
            />
        ));
    }, [environments, deployments, showEnvironmentDetails, showDeploymentDetails, environmentsPermissions]);

    return (
        <div className="card">
            <div className="card-header border-bottom">
                {renderToolbar}
            </div>
            <div className="card-body">
                <Accordion>
                    {renderAccordion}
                </Accordion>
            </div>
            <CreateDeployment show={showCreateDeploymentModal}
                domain={domain}
                onCancel={() => setShowCreateDeploymentModal(false)} />
        </div>
    );
};

export default EnvironmentList;
